import classNames from 'classnames';
import moment from 'moment';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { AdvancedSelect, Decorator, Input } from '@myhealth-raccoon/components';
import { validation } from '@myhealth-raccoon/forms';

import { PatientHealthInfo } from 'src/services/patient';
import { FundCode, FundCodes } from 'src/utils/fundCodes';

export type UpdatePHIFormValues = {
    havePhi: 'phi' | 'oshc' | 'no';
    providerId: string | number;
    providerName: string;
    customerId: string;
    expiry: string;
};

type Props = {
    className?: string;
    onSubmit?(values: UpdatePHIFormValues): unknown;
    patientHealthInfo: PatientHealthInfo;
    setIsDirty?(isDirty: boolean): void;
    required?: boolean;
};

export interface PHIFormRef {
    submit(): Promise<UpdatePHIFormValues>;
}

export const UpdatePHIForm = forwardRef<PHIFormRef, Props>(
    ({ className, onSubmit, patientHealthInfo, setIsDirty, required }, ref) => {
        const preExistingInformation = !!patientHealthInfo.health_insurance_no;
        const [isEditing, setIsEditing] = useState(!preExistingInformation);

        const defaultValues: Partial<UpdatePHIFormValues> = useMemo(() => {
            const fund = FundCodes.find((f) => f.FundName === patientHealthInfo?.health_insurance_name);
            return {
                havePhi: preExistingInformation ? 'phi' : undefined,
                customerId: patientHealthInfo?.health_insurance_no,
                providerId: patientHealthInfo?.health_insurance_name ? (fund?.FundID ?? 9999) : '',
                providerName: patientHealthInfo ? (fund?.FundName ?? patientHealthInfo.health_insurance_name) : '',
                expiry:
                    patientHealthInfo?.health_insurance_expiry_date &&
                    (patientHealthInfo.health_insurance_expiry_date !== '2099-12-12'
                        ? moment(patientHealthInfo.health_insurance_expiry_date).format('yyyy-MM')
                        : undefined), // Adjust for month input.
            };
        }, [patientHealthInfo, preExistingInformation]);

        const {
            handleSubmit,
            register,
            formState: { errors, isSubmitting, isDirty },
            watch,
            setValue,
        } = useForm<UpdatePHIFormValues>({ defaultValues });

        const havePhi = watch('havePhi');

        const unfinishedEditing = havePhi !== 'no' && isDirty;

        useEffect(() => {
            setIsDirty?.(unfinishedEditing);
        }, [unfinishedEditing, setIsDirty]);

        useImperativeHandle(ref, () => ({
            submit: async () => {
                return new Promise((resolve, reject) => {
                    handleSubmit(resolve, reject)();
                });
            },
        }));

        const validateExpiry = useCallback((value: unknown): string | undefined => {
            if (typeof value !== 'string' || !value) {
                return undefined;
            }
            const parsed = moment(value, 'YYYY-DD');
            if (
                !parsed.isValid() ||
                parsed.isAfter(moment().add(10, 'year')) ||
                parsed.isBefore(moment().subtract(10, 'year'))
            ) {
                return 'Enter a valid date';
            }
            return undefined;
        }, []);

        register('providerId', {
            validate: havePhi === 'phi' || havePhi === 'oshc' ? validation.required() : undefined,
        });

        const havePhiField = register('havePhi', {
            validate: required ? validation.required() : undefined,
        });

        const innerForm = useCallback(() => {
            return (
                <div
                    className={classNames('flex flex-col gap-5 rounded-xl border bg-gray-50 px-4 py-5', {
                        'border-warning-300 group-focus-within:border-gray-200': isDirty && !isSubmitting,
                        'border-gray-200': !isDirty || isSubmitting,
                    })}
                >
                    <Decorator label="Who is your private health insurer?" fieldError={errors.providerId}>
                        <AdvancedSelect<FundCode, false>
                            name="providerId"
                            options={FundCodes}
                            error={!!errors.providerId}
                            value={FundCodes.find((f) => `${f.FundID}` === `${watch('providerId')}`)}
                            maxMenuHeight={200}
                            getOptionLabel={(o) => o.FundName}
                            onChange={(o) => {
                                setValue('providerId', o?.FundID ?? undefined!);
                                if (o && o.FundID !== 9999) {
                                    setValue('providerName', o.FundName);
                                } else {
                                    setValue('providerName', '');
                                }
                            }}
                            isOptionSelected={(o) => `${o.FundID}` === `${watch('providerId')}`}
                            blurInputOnSelect
                        />
                    </Decorator>
                    {watch('providerId') === 9999 && (
                        <Input
                            {...register('providerName', {
                                validate: validation.composeValidators(
                                    validation.required(),
                                    validation.regex(/^[A-Za-z\s\d-_]*$/, 'Please use only letters, number & hyphens.')
                                ),
                            })}
                            fieldError={errors.providerName}
                            placeholder="Please enter the name of your health insurer"
                        />
                    )}

                    <div className="flex flex-col gap-4 sm:flex-row">
                        <Input
                            {...register('customerId', {
                                validate: validation.composeValidators(
                                    validation.required(),
                                    validation.regex(/^[A-Za-z\s\d-_]*$/, 'Please use only letters, number & hyphens.')
                                ),
                            })}
                            id="customerId"
                            fieldError={errors.customerId}
                            label="Health Insurance no."
                            className="flex-1"
                        />
                        <Input
                            {...register('expiry', { validate: validateExpiry })}
                            id="expiry"
                            label={
                                <>
                                    Expiry <small className="text-sm font-normal text-gray-700">(optional)</small>
                                </>
                            }
                            type="month"
                            className="flex-1"
                            fieldError={errors.expiry}
                        />
                    </div>

                    <div className="ml-auto flex w-fit flex-row gap-3">
                        {onSubmit && (
                            <button
                                disabled={isSubmitting}
                                type="submit"
                                className="text-primary-700 font-semibold disabled:text-gray-600"
                            >
                                Edit
                            </button>
                        )}
                    </div>
                </div>
            );
        }, [
            errors.customerId,
            errors.expiry,
            errors.providerId,
            errors.providerName,
            isDirty,
            isSubmitting,
            onSubmit,
            register,
            setValue,
            watch,
            validateExpiry,
        ]);

        if (!isEditing && preExistingInformation) {
            return (
                <div className="flex flex-col gap-2 rounded-xl border bg-gray-50 p-4">
                    <p className="text-sm text-gray-700">
                        <strong className="font-medium">{patientHealthInfo.health_insurance_name}</strong>
                        <br />
                        {patientHealthInfo.health_insurance_no}
                    </p>
                    <div className="flex flex-row gap-3">
                        {/* TODO: automed doesn't currently allow PHI deletion
                        <button className="text-gray-600 font-semibold hover:underline" type="button">Remove</button>
                         */}
                        <button
                            type="button"
                            className="text-primary-700 text-sm font-semibold hover:underline"
                            onClick={() => setIsEditing(true)}
                        >
                            Update
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <form onSubmit={onSubmit && handleSubmit(onSubmit)} className={classNames('group', className)}>
                <Decorator
                    fieldset
                    label="Do you have any of the following health cover?"
                    requiredIndicator={required}
                    fieldError={errors.havePhi}
                >
                    <div className="flex flex-col gap-2 text-sm">
                        <Decorator className="flex flex-row gap-2">
                            <input
                                type="radio"
                                className="radio mt-0.5 text-base"
                                value="phi"
                                {...havePhiField}
                                id="phi_phi"
                            />
                            <label htmlFor="phi_phi" className="cursor-pointer text-gray-700">
                                Private Health Insurance
                            </label>
                        </Decorator>
                        {havePhi === 'phi' && innerForm()}
                        <Decorator className="flex flex-row gap-2">
                            <input
                                type="radio"
                                className="radio mt-0.5 text-base"
                                value="oshc"
                                {...havePhiField}
                                id="phi_oshc"
                            />
                            <label htmlFor="phi_oshc" className="cursor-pointer text-gray-700">
                                Overseas Student/Visitor Health Cover (OSHC / OVHC)
                            </label>
                        </Decorator>
                        {havePhi === 'oshc' && innerForm()}
                        <Decorator className="flex flex-row gap-2">
                            <input
                                type="radio"
                                className="radio mt-0.5 text-base"
                                value="no"
                                {...havePhiField}
                                id="phi_no"
                            />
                            <label htmlFor="phi_no" className="cursor-pointer text-gray-700">
                                No
                            </label>
                        </Decorator>
                    </div>
                </Decorator>
                {onSubmit && unfinishedEditing && (
                    <p className="text-warning mt-2 group-focus-within:hidden">
                        Please finish updating your Private Health Insurance
                    </p>
                )}
            </form>
        );
    }
);
